<template>
  <Popup
    :isPopupOpen="paymentState.visible"
    @close-popup="$emit('close-popup')"
    @after-leave="$emit('after-leave')"
  >
    <div class="bg-white radius-medium pd-medium w-100 w-max-30r">

      <!-- Step 1: Select Provider -->
      <div v-if="step === 'select-provider'">
        <h3 class="h5 mn-b-regular">{{ $t('payments.popup.selectProvider') }}</h3>

        <ProviderSelector
          v-model="selectedProvider"
          :providers="providers"
          class="mn-b-medium"
        />

        <div v-if="!paymentState.amount" class="mn-b-medium">
          <label class="p-small t-grey mn-b-thin d-block">{{ $t('payments.amount') }}</label>
          <Field v-model="inputAmount" type="number" :placeholder="$t('payments.amount')" />
        </div>

        <Button
          :disabled="!selectedProvider || (!paymentState.amount && !inputAmount)"
          @click="confirmPayment"
          class="w-100"
        >
          {{ $t('payments.popup.confirm') }}
        </Button>
      </div>

      <!-- Step 2: Processing -->
      <div v-else-if="step === 'processing'" class="d-flex flex-column flex-v-center pd-big">
        <Loader class="mn-b-medium" />
        <p class="t-grey">{{ $t('payments.popup.processing') }}</p>
      </div>

      <!-- Step 3: Success -->
      <div v-else-if="step === 'success'" class="t-center pd-big">
        <IconCheckmark class="i-extra t-main mn-b-medium" :fill="'rgb(var(--main))'" />
        <h3 class="h5 mn-b-small">{{ $t('payments.popup.success') }}</h3>
        <Button @click="closePayment(currentPayment)" class="w-100 mn-t-medium">OK</Button>
      </div>

      <!-- Step 4: Error -->
      <div v-else-if="step === 'error'" class="t-center pd-big">
        <IconCross class="i-extra mn-b-medium" :fill="'rgb(var(--red))'" />
        <h3 class="h5 mn-b-small t-red">{{ $t('payments.popup.failed') }}</h3>
        <p class="t-grey p-small mn-b-medium">{{ errorMessage }}</p>
        <Button @click="step = 'select-provider'" class="w-100">{{ $t('payments.popup.cancel') }}</Button>
      </div>
    </div>
  </Popup>
</template>

<script setup>
import { ref, onMounted, inject } from 'vue';
import Popup from '@martyrs/src/design-system/components/Popup/Popup.vue';
import Button from '@martyrs/src/design-system/components/Button/Button.vue';
import Field from '@martyrs/src/design-system/components/Field/Field.vue';
import Loader from '@martyrs/src/design-system/components/Loader/Loader.vue';
import IconCheckmark from '@martyrs/src/modules/icons/navigation/IconCheckmark.vue';
import IconCross from '@martyrs/src/modules/icons/navigation/IconCross.vue';
import ProviderSelector from './ProviderSelector.vue';

const props = defineProps({
  paymentState: { type: Object, required: true },
  closePayment: { type: Function, required: true },
});

const store = inject('store');
const step = ref('select-provider');
const selectedProvider = ref(null);
const inputAmount = ref(null);
const providers = ref([]);
const currentPayment = ref(null);
const errorMessage = ref('');

onMounted(async () => {
  try {
    providers.value = await store.payments.actions.getProviders();
    if (providers.value.length === 1) {
      selectedProvider.value = providers.value[0];
      // 1 провайдер — сразу редирект, без попапа
      confirmPayment();
    }
  } catch (err) {
    console.error('Failed to load providers:', err);
  }
});

async function confirmPayment() {
  step.value = 'processing';

  try {
    const amount = props.paymentState.amount || Number(inputAmount.value);
    const url = new URL(window.location.href);
    url.searchParams.set('payment', 'success');
    const returnUrl = url.toString();

    const result = await store.payments.actions.createPayment({
      provider: selectedProvider.value,
      amount,
      currency: props.paymentState.currency,
      description: props.paymentState.description,
      returnUrl,
      metadata: props.paymentState.metadata,
    });

    currentPayment.value = result;

    // Redirect-based confirmation (YooKassa default)
    if (result.confirmationUrl) {
      window.location.href = result.confirmationUrl;
      return;
    }

    // Wallet or instant payment — already succeeded
    if (result.status === 'succeeded') {
      step.value = 'success';
      return;
    }

    // Pending — wait for webhook
    step.value = 'processing';
  } catch (error) {
    errorMessage.value = error.response?.data?.message || error.message;
    step.value = 'error';
  }
}
</script>
